import * as _ from 'lodash';
import type { MouseEvent, VFC, FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cx from 'classnames';
import * as GlobalLicenseCorrectionsStore from '../../../redux/globalLicenseCorrections';
import type { Match, MatchOverride, MatchOverrideWithHashFilter, MatchOverrideWithLicenseFilter, MatchOverrideWithNewLicense, RevisionLicense } from '../../../types';
import { Icon, IconButton, icons } from '../../generic';
import Accordion from '../../generic/Accordion';
import css from './LicenseEditor.module.less';
import { LoadingBoundary } from '../../generic/LoadingBoundary';
import type { MatchAndOverrides } from '../../../redux/globalLicenseCorrections/types';

type NewLicenseOverrideProps = {
  override: MatchOverrideWithNewLicense;
  isLoading: boolean;
  onRevert: (override: MatchOverride) => (e: MouseEvent) => void;
};

const NewLicenseOverride: VFC<NewLicenseOverrideProps> = ({ override, isLoading, onRevert }) =>
  <div className={cx(css.override, css.override__created)}>
    <span>
      <span className={css.icon}>
        <Icon icon={icons.warning} />
      </span>
      This license will always be present due to an override.
    </span>
    <IconButton
      id="revert-license"
      icon={icons.undo}
      tooltip="Revert this override"
      onClick={onRevert(override)}
      isDisabled={isLoading}
    />
  </div>;

type LicenseChangeOverrideProps = {
  override: MatchOverrideWithLicenseFilter;
  isLoading: boolean;
  onRevert: (override: MatchOverride) => (e: MouseEvent) => void;
};

const LicenseChangeOverride: VFC<LicenseChangeOverrideProps> = ({ override, isLoading, onRevert }) =>
  <div className={cx(css.override, {
    [css.override__reclassified]: override.newLicenseIdOrRemove,
    [css.override__removed]: !override.newLicenseIdOrRemove,
  })}>
    {
      override.newLicenseIdOrRemove
        ?
          <span>
            <span className={css.icon}>
              <Icon icon={icons.warning} />
            </span>
            All matches for this license will be reclassified as {override.newLicenseIdOrRemove}
          </span>
        :
          <span>
            <span className={css.icon}>
              <Icon icon={icons.warning} />
            </span>
            All matches for this license will be removed
          </span>
    }
    <IconButton
      id="revert-license"
      icon={icons.undo}
      tooltip="Revert this override"
      onClick={onRevert(override)}
      isDisabled={isLoading}
      />
  </div>;

/** A comparison function to sort by path. */
function compareMatchAndOverrideEntryByPath(a: MatchAndOverrides, b: MatchAndOverrides) {
  const aPath = a.match?.path || a.overrides[0]?.matchFilter.path || a.overrides[0]?.matchFilter.matchIdentityHash || '';
  const bPath = b.match?.path || b.overrides[0]?.matchFilter.path || b.overrides[0]?.matchFilter.matchIdentityHash || '';

  if (aPath < bPath) {
    return -1;
  }
  if (aPath > bPath) {
    return 1;
  }
  return 0;
}

/**
 * Lists licenses for the currently selected project/revision and allows the
 * user to configure overrides.
 */
export const LicenseEditor: VFC = () => {
  const licensesAndOverridesValue = useSelector(GlobalLicenseCorrectionsStore.selectors.licensesAndOverridesSelector);

  return (
    <LoadingBoundary
      asyncValue={licensesAndOverridesValue}
      renderValue={({ value: licensesAndOverrides }) => {
        return (
          <div>
            {
              licensesAndOverrides.map(licenseAndOverrides =>
                <LicenseMatches
                  key={licenseAndOverrides.licenseId}
                  licenseId={licenseAndOverrides.licenseId}
                  licenses={licenseAndOverrides.licenses}
                  newLicenseOverrides={licenseAndOverrides.newOverrides}
                  matches={licenseAndOverrides.matches}
                  licenseOverrides={licenseAndOverrides.licenseOverrides} />,
              )
            }
          </div>);
      }}
      renderEmpty={<div>empty</div>}
      renderError={({ errors }) => <div>error: ${errors.join('. ')}</div>}
      renderLoading={<div>loading</div>}
      />);
};

type LicenseMatchesProps = {
  licenseId: string;
  licenses: RevisionLicense[];
  newLicenseOverrides: MatchOverrideWithNewLicense[];
  matches: MatchAndOverrides[];
  licenseOverrides: MatchOverrideWithLicenseFilter[];
};

/**
 * Lists a license as an accordion that contains its matched paths.
 */
const LicenseMatches: FC<LicenseMatchesProps> = (
  { licenseId, licenses, newLicenseOverrides, matches, licenseOverrides },
) => {
  const dispatch = useDispatch();

  const project = useSelector(GlobalLicenseCorrectionsStore.selectors.projectSelector);

  const onRemove = (e: MouseEvent) => {
    if (!project.value) {
      return;
    }
    dispatch(GlobalLicenseCorrectionsStore.actions.removeLicense(project.value, licenseId));
    e.stopPropagation();
  };

  const onRevert = (override: MatchOverride) => (e: MouseEvent) => {
    dispatch(GlobalLicenseCorrectionsStore.actions.revertOverride(override));
    e.stopPropagation();
  };

  return (
    <Accordion key={licenseId} id={`licenses-${licenseId}`} isInitiallyOpen>
      <Accordion.Target>
        <div className={css.licenseHeader}>
          <span className={cx({
            [css.licenseOverride]: !_.isEmpty(licenseOverrides),
          })}>
            {licenseId ?? licenseOverrides.find(override => override.matchFilter.licenseId)?.matchFilter.licenseId ?? '(no license)'}
          </span>
          {
            _.isEmpty(licenseOverrides) && _.isEmpty(newLicenseOverrides) && !_.isEmpty(licenses) &&
              <span className={css.removeButton}>
                <IconButton
                  id="remove-license"
                  icon={icons.remove}
                  tooltip="Remove this license"
                  onClick={onRemove}
                  isDisabled={project.isLoading}
                  />
              </span>
          }
        </div>
      </Accordion.Target>
      <Accordion.Body>
        {
          newLicenseOverrides.map(newLicenseOverride =>
            <NewLicenseOverride
              key={newLicenseOverride.id}
              override={newLicenseOverride}
              isLoading={project.isLoading}
              onRevert={onRevert} />)
        }
        {
          licenseOverrides.map(override =>
            <LicenseChangeOverride
              key={override.id}
              override={override}
              isLoading={project.isLoading}
              onRevert={onRevert} />)
        }
        {
          _.isEmpty(matches)
            ?
              <div className={cx(css.matches, css.noMatches)}>
                {
                  newLicenseOverrides
                    ? `No matches found.  The license should show up as declared during the next analysis.`
                    : `No matches found.  Empty licenses are generally removed during analysis.`
                }
              </div>
            :
              <ul className={css.matches}>
                { matches
                  .sort(compareMatchAndOverrideEntryByPath)
                  .map(({ match, overrides: matchOverrides }: MatchAndOverrides) => {
                    return (<MatchItem
                    // One of these two should always contain a hash, so the fall-through is just for typing.
                      key={match?.matchIdentityHash || matchOverrides[0]?.matchFilter.matchIdentityHash || 'no-hash'}
                      match={match}
                      overrides={matchOverrides}
                      isOverridden={!_.isEmpty(matchOverrides) || !_.isEmpty(licenseOverrides)} />);
                  })}
              </ul>
        }
      </Accordion.Body>
    </Accordion>
  );
};

type MatchItemProps = {
  match?: Match,
  overrides: MatchOverrideWithHashFilter[],
  isOverridden: boolean,
};

/** An item in the list of matches */
const MatchItem: FC<MatchItemProps> = ({ match, overrides, isOverridden }) => {
  const dispatch = useDispatch();

  const project = useSelector(GlobalLicenseCorrectionsStore.selectors.projectSelector);

  const onRemove = () => {
    if (!project.value || !match) {
      return;
    }
    dispatch(GlobalLicenseCorrectionsStore.actions.removeMatch(project.value, match));
  };

  const onRevert = async () => {
    await Promise.all(overrides.map(matchOverride =>
      dispatch(GlobalLicenseCorrectionsStore.actions.revertOverride(matchOverride)),
    ));
  };

  return (
    <li
      // One of these two should always contain a hash, so the fall-through is just for typing.
      key={match?.matchIdentityHash || overrides[0]?.matchFilter.matchIdentityHash || 'no-hash'}
      className={cx(css.match, {
        // This handles the case that the override is there, but nothing matches it anymore.
        [css.itemNotPresent]: match === null,
      })}>
      <span>
        <span className={cx({
          [css.matchOverride]: isOverridden,
          [css.itemNotPresent]: !match,
        })}>
          {match?.path || overrides[0]?.matchFilter.path || overrides[0]?.matchFilter.matchIdentityHash }
        </span>
        { isOverridden && match && <span> (pending reanalysis)</span>}
      </span>
      {
        !_.isEmpty(overrides) &&
          <IconButton
            id="revert-match"
            icon={icons.undo}
            tooltip="Revert this override"
            onClick={onRevert}
            isDisabled={project.isLoading}
            />
      }
      {
        !isOverridden &&
        <span className={css.removeButton}>
          <IconButton
            id="remove-match"
            icon={icons.remove}
            tooltip="Remove this match"
            onClick={onRemove}
            isDisabled={project.isLoading}
            />
        </span>
      }
    </li>
  );
};
